import {useCallback, useRef} from 'react';
import {useFormikContext} from 'formik';
import {Drawer, DrawerFields} from 'components/customer/Product/entity/Drawer';
import {
    FaceRange,
    getFaceHeight,
    readjustLastAutoHeight,
    toNumber,
} from 'components/customer/Product/Drawer/helpers';
import {
    DragLimits,
    FaceEdge,
    FacePreview,
    roundHeight,
    useDragGesture,
} from 'components/customer/Product/Drawer/helpers/useDragGesture';

// the carcase reaches for these through the hook it already imports
export type {FaceEdge, FacePreview};

interface UseFaceDragProps {
    faceHeightFieldName: string;
    styleFieldName: string;
    ratio: number;
    total: number;
    top: number;
    bottom: number;
    gap: number;
    rangeFor: (index: number) => FaceRange;
}

// A drag fixes the dragged drawer and only the autos absorb, so a height set on
// purpose is never moved. Nothing reaches the form until the drag ends.
export const useFaceDrag = ({
    faceHeightFieldName,
    styleFieldName,
    ratio,
    total,
    top,
    bottom,
    gap,
    rangeFor,
}: UseFaceDragProps) => {
    const {values, setFieldValue, setTouched} =
        useFormikContext<DrawerFields>();

    // the listeners the gesture attaches outlive the render they came from
    const valuesRef = useRef(values);
    valuesRef.current = values;
    const rangeRef = useRef(rangeFor);
    rangeRef.current = rangeFor;

    const limits = useCallback(
        (dragged: number): DragLimits => ({
            draggedHeight: toNumber(
                valuesRef.current.drawers?.[Number(dragged)]?.[
                    String(faceHeightFieldName)
                ]
            ),
            ...rangeRef.current(dragged),
        }),
        [faceHeightFieldName]
    );

    // reshares across the autos, or they sit stale until the drag ends
    const settle = useCallback(
        (dragged: number, nextHeight: number) => {
            const drawers = (valuesRef.current.drawers ?? []).map(
                (drawer) =>
                    ({
                        drawer_face_height: toNumber(
                            drawer[String(faceHeightFieldName)]
                        ),
                        drawer_style: toNumber(drawer[String(styleFieldName)]),
                    }) as Drawer
            );

            drawers[Number(dragged)].drawer_face_height = nextHeight;
            drawers[Number(dragged)].drawer_style = 0;

            drawers.forEach((drawer, position) => {
                if (drawer.drawer_style == 1) {
                    drawer.drawer_face_height = getFaceHeight(
                        drawers,
                        position,
                        total,
                        bottom,
                        top,
                        gap
                    );
                }
            });

            return readjustLastAutoHeight(drawers, total, bottom, top, gap).map(
                (drawer) => drawer.drawer_face_height
            );
        },
        [bottom, faceHeightFieldName, gap, styleFieldName, top, total]
    );

    const setHeight = useCallback(
        (index: number, height: number) => {
            void setTouched({drawers: true});
            void setFieldValue(
                `drawers[${index}].${faceHeightFieldName}`,
                roundHeight(height)
            );
            void setFieldValue(`drawers[${index}].${styleFieldName}`, 0);
        },
        [faceHeightFieldName, setFieldValue, setTouched, styleFieldName]
    );

    const {dragging, preview, startDrag, nudge} = useDragGesture({
        ratio,
        limits,
        settle,
        commit: setHeight,
    });

    return {dragging, preview, startDrag, nudge, setHeight};
};
